<template>
    <div :class="classList">
        <div v-for="(item, index) in productList" :key="index" class="group h-full">
            <div class="flex h-full flex-col overflow-hidden rounded-[8px] border border-[#ECE8E1] bg-white dark:border-white/10 dark:bg-dark-secondary transition duration-300 hover:-translate-y-1 hover:border-primary/25 hover:shadow-[0_20px_45px_rgba(17,24,39,0.08)]">
            <div class="relative overflow-hidden bg-[#F6F2EC] dark:bg-title/40 h-[240px] sm:h-[280px] lg:h-[320px] flex-none">
                <router-link :to="`/product-details/${item.id}`" class="block w-full h-full">
                    <img class="w-full h-full object-cover object-center transform group-hover:scale-[1.04] duration-500" :src="item.image || PRODUCT_PLACEHOLDER" alt="shop"/>
                </router-link>
                <div class="pointer-events-none absolute inset-0 bg-gradient-to-t from-title/35 via-title/10 to-transparent opacity-0 duration-300 group-hover:opacity-100"></div>

                <div v-if="item.tag === 'Hot Sale'" class="absolute z-10 top-3 left-3 py-1.5 px-2.5 bg-[#1CB28E] rounded-full font-primary text-[11px] text-white font-semibold leading-none">
                    Hot Sale
                </div>

                <div v-if="item.tag === 'NEW'" class="absolute z-10 top-3 left-3 py-1.5 px-2.5 bg-[#9739E1] rounded-full font-primary text-[11px] text-white font-semibold leading-none">
                    NEW
                </div>
                <div v-if="item.tag && item.tag.includes('OFF')" class="absolute z-10 top-3 left-3 py-1.5 px-2.5 bg-[#E13939] rounded-full font-primary text-[11px] text-white font-semibold leading-none">
                    {{ item.tag }}
                </div>
                <div
                    v-if="item.hasAr"
                    title="View this piece in your room"
                    class="absolute z-10 top-3 right-3 flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1.5 font-primary text-[11px] font-semibold leading-none text-title shadow-sm backdrop-blur-sm dark:bg-title dark:text-white"
                >
                    <i class="mdi mdi-cube-scan text-[13px] text-primary"></i>
                    3D
                </div>
                <div class="product-card__actions absolute z-10 left-1/2 bottom-3 flex -translate-x-1/2 translate-y-2 items-center gap-1.5 rounded-full p-1.5 shadow-md backdrop-blur-sm opacity-0 duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                    <button
                        type="button"
                        :title="wishlist.isInWishlist(item.id) ? 'Saved to wishlist' : 'Add to wishlist'"
                        class="product-card__action product-card__action--soft"
                        @click="handleWishlist(item)"
                    >
                        <i :class="wishlist.isInWishlist(item.id) ? 'fa-solid fa-heart text-[14px]' : 'mdi mdi-cards-heart-outline text-[16px]'"></i>
                    </button>
                    <button
                        type="button"
                        :title="item.inStock === false ? 'Out of stock' : 'Add to cart'"
                        :disabled="item.inStock === false"
                        class="product-card__action product-card__action--primary"
                        @click="handleCart(item)"
                    >
                        <i class="mdi mdi-shopping-outline text-[16px]"></i>
                    </button>
                    <button
                        type="button"
                        title="Open product details"
                        class="product-card__action product-card__action--soft"
                        @click="openProductDetails(item)"
                    >
                        <i class="mdi mdi-eye-outline text-[16px]"></i>
                    </button>
                </div>
            </div>
            <div class="flex flex-col gap-2 px-4 pb-4 pt-3 h-[130px] sm:h-[140px] flex-none">
                <span v-if="item.categoryName" class="text-[10px] font-medium uppercase tracking-[0.16em] text-paragraph dark:text-white-light truncate">
                    {{ item.categoryName }}
                </span>
                <div class="flex items-center gap-2 flex-wrap">
                    <h4 class="font-semibold leading-none dark:text-white text-base">{{item.price}}</h4>
                    <span v-if="item.originalPrice" class="text-xs leading-none text-paragraph line-through dark:text-white-light">{{ item.originalPrice }}</span>
                </div>
                <div class="mt-auto">
                    <h5 class="font-medium dark:text-white text-[15px] sm:text-[16px] leading-[1.3] line-clamp-2">
                        <router-link :to="`/product-details/${item.id}`" class="duration-300 hover:text-primary">
                            {{item.name}}
                        </router-link>
                    </h5>
                </div>
            </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { defineProps, onMounted } from 'vue';
    import { PRODUCT_PLACEHOLDER } from '@/lib/placeholder';
    import { useRouter } from 'vue-router';
    import { useCart } from '@/composables/use-cart';
    import { useWishlist } from '@/composables/use-wishlist';
    import { useClientSession } from '@/composables/use-client-session';

    defineProps({
        productList:Array,
        classList:String
    })

    const router = useRouter();
    const cart = useCart();
    const wishlist = useWishlist();
    const session = useClientSession();

    function handleCart(item) {
        // Nothing to sell, and the order endpoint would refuse it anyway.
        if (item.inStock === false) {
            return;
        }

        // The list endpoint sends a boolean rather than every group and option,
        // so fall back to counting only when handed a full record (the detail
        // endpoint, and the wishlist page which maps its own rows).
        const hasModifiers = item.raw?.has_modifiers ?? (item.raw?.modifiers || []).length > 0;

        if (hasModifiers) {
            router.push(`/product-details/${item.id}`);
            return;
        }

        cart.addItem({
            product_id: item.id,
            name: item.name,
            image: item.image,
            category_name: item.categoryName,
            quantity: 1,
            base_price: item.numericPrice,
            stock_quantity: item.stockQuantity,
            selected_modifiers: [],
        });
    }

    function openProductDetails(item) {
        router.push(`/product-details/${item.id}`);
    }

    async function handleWishlist(item) {
        if (!session.isAuthenticated.value) {
            router.push('/login');
            return;
        }

        if (wishlist.isInWishlist(item.id)) {
            await wishlist.remove(item.id);
            return;
        }

        await wishlist.add(item.id);
    }

    onMounted(async () => {
        try {
            await session.initializeSession();
        } catch (error) {
            // Invalid tokens are cleared by the session composable.
        }

        if (session.isAuthenticated.value) {
            await wishlist.load();
        }
    });

</script>

<style>
/* Hover action bar on product cards. Plain CSS rather than dark: utilities — the
   precompiled stylesheet lacks dark:bg-title/90 and dark:hover:bg-white, which left
   the bar white (and the heart button invisible) in dark mode. */
.product-card__actions {
    background: rgba(255, 255, 255, 0.95);
}

.dark .product-card__actions {
    background: rgba(30, 42, 53, 0.94);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
}

.product-card__action {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 999px;
    transition: background-color 0.3s ease, color 0.3s ease;
}

.product-card__action--soft {
    background: #f6f2ec;
    color: #172430;
}

.product-card__action--soft:hover {
    background: #bb976d;
    color: #fff;
}

.dark .product-card__action--soft {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
}

.dark .product-card__action--soft:hover {
    background: #bb976d;
}

.product-card__action--primary {
    background: #bb976d;
    color: #fff;
}

.product-card__action--primary:hover {
    background: #172430;
}

.dark .product-card__action--primary:hover {
    background: #fff;
    color: #172430;
}

.product-card__action:disabled,
.product-card__action:disabled:hover {
    background: #bb976d;
    color: #fff;
    opacity: 0.4;
    cursor: not-allowed;
}
</style>
